import React, {useState} from "react";
import {connect} from "react-redux";
import { PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import {
    Button,
    Col,
    Divider,
    Dropdown,
    Input as AntdInput,
    Menu,
    Row,
    Select,
    Spin,
    Switch,
    Tabs,
    Tooltip,
    Typography,
    Form
} from "antd";
import MonacoEditor from "react-monaco-editor";
import {fetch} from "../../../../../../api/rest";
import {SubmissionError} from "redux-form";
import {addAction as addPanelAction, removeAction as removePanelAction} from "../../../../../../actions/panel";
import {
    PANEL_COOKIECONSENT_SITE_GROUP_SERVICE_EDIT_DEFAULT,
} from "../../../../../../factories/panel";
import {translationLanguages} from "../../../../directory/translationLanguages";
import { RULES} from "../../../../directory/siteServiceRules";
import RemoteSelect from "../../../../../common/form/RemoteSelect";
import SiteServiceRule from "../../../../common/form/SiteServiceRule";
import {randomString} from "../../../../../../utils/randomString";
import EntityLoader from "../../../../../common/EntityLoader";
import {getEntityById} from "../../../../../../selectors/entity";
import {submissionErrorToFormFields} from "../../../../../../utils/submissionErrorToFormFields";

class Input extends React.Component {
    render() {
        return <AntdInput autoComplete="none" {...this.props}/>;
    }
}

const EntityName = ({entity}) => <small> {entity && entity.name}</small>;

export default (connect(
    (state, {groupId}) => ({
        group: getEntityById(state, groupId),
    }),
    dispatch => ({
        onAddPanel: (component, parentKey) => dispatch(addPanelAction(component, parentKey)),
        onRemovePanel: (uniqueKey) => dispatch(removePanelAction(uniqueKey)),
    })
)(({onAddPanel, onRemovePanel, group, groupId}) => {
    const [loading, setLoading] = useState(false);
    const [translations, setTranslations] = useState({});
    const [activeTranslation, setActiveTranslation] = useState();
    const [isSubService, setIsSubService] = useState(false);
    const [form] = Form.useForm();

    const handleSubmit = values => {

        values.site = group.site;
        values.group = groupId;

        values.translations = values.translations || {};
        Object.keys(values.translations).forEach((key) => {
            ['name', 'policyUrl', 'purpose'].forEach(prop => {
                if (String(values.translations[key][prop]).length === 0) {
                    values.translations[key][prop] = null;
                }
            });
        });

        values.theme = values.theme || {};
        values.theme.rules = values.theme.rules || [];

        setLoading(true);

        return fetch('/cookieconsent/sites/defaultservices', {method: 'POST', body: JSON.stringify(values)})
            .then(data => {
                setLoading(false);
                onAddPanel(
                    {
                        type: PANEL_COOKIECONSENT_SITE_GROUP_SERVICE_EDIT_DEFAULT,
                        uid: `cookieconsentsitegroupservices:${data['@id']}:edit:default`,
                        config: {
                            groupId: groupId,
                            serviceId: data['@id']
                        }
                    },
                    `cookieconsentsitegroupservices:${groupId}:new:default`
                );
                onRemovePanel(`cookieconsentsitegroupservices:${groupId}:new:default`);
            })
            .catch(e => {
                if (e instanceof SubmissionError) {
                    form.setFields(submissionErrorToFormFields(e, form));
                }
            })
            .finally(() => setLoading(false));
    };

    const onTranslationTabsEdit = (targetKey, action) => {
        const t = {...translations};

        switch (action) {
            default:
                break;

            case 'add':
                t[targetKey] = {};
                setTranslations(t);
                setActiveTranslation(targetKey);
                break;

            case 'remove':
                delete t[targetKey];
                setTranslations(t);
                break;
        }
    };

    const [themeRules, setThemeRules] = useState({});
    const onThemeRules = (targetKey, action) => {
        const t = {...themeRules};

        switch (action) {
            default:
                break;

            case 'add':
                t[randomString()] = RULES[targetKey];
                setThemeRules(t);
                break;

            case 'remove':
                delete t[targetKey];
                setThemeRules(t);
                break;
        }
    };

    return (
        <Spin spinning={loading} size="large">
            <Typography.Title level={3}>
                Neuer Dienst
                <br />
                <EntityLoader entityId={groupId}>
                    <EntityName/>
                </EntityLoader>
            </Typography.Title>

            <Form layout="vertical" onFinish={handleSubmit} form={form}>

                <Typography.Title level={4} type="secondary">Basisdaten</Typography.Title>
                <Row gutter={10} type="flex" justify="space-between">
                    <Col span={11}>
                        <Form.Item label="Basisdienst" name={'service'} rules={[{required: true, message: 'Dieser Wert sollte nicht leer sein.'}]}>
                            <RemoteSelect
                                allowClear
                                queryParam="name"
                                render={item => <Select.Option key={item.id} value={item['@id']}>
                                    <>
                                        {item.name}
                                        <EntityLoader entityId={item.provider}>
                                            <EntityName/>
                                        </EntityLoader>
                                    </>
                                </Select.Option>}
                                resourceUrl="cookieconsent/providers/services"
                            />
                        </Form.Item>

                        <Row gutter={10}>
                            <Col span={12}>
                                <Form.Item label="Unveränderlich" name={'immutable'} valuePropName={'checked'}>
                                    <Switch/>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Sub-Dienst">
                                    <Switch onChange={setIsSubService} checked={isSubService}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={11}>
                        {isSubService && <Form.Item key="parent" label="Übergeordneter Dienst" name={'parent'}>
                            <RemoteSelect allowClear resourceUrl={`${groupId}/services`}/>
                        </Form.Item>}
                    </Col>
                </Row>

                <Form.Item label="Markup" name={'markup'} rules={[{type: 'string'}]}>
                    <MonacoEditor height={200} width="100%" language="javascript" theme="vs-dark"/>
                </Form.Item>

                <Divider/>
                <Typography.Title level={4} type="secondary">Modifikationen</Typography.Title>
                <small>Mit dem Basisdienst identische Werte werden nicht überschrieben.</small>
                <Row gutter={10} type="flex" justify="space-between">
                    <Col span={11}>
                        <Form.Item label="Name" name={"name"} rules={[{type: 'string'}]}>
                            <Input/>
                        </Form.Item>
                    </Col>
                    <Col span={11}>
                        <Form.Item label="URL zu Vertragsbedingungen" name={"policyUrl"} rules={[{type: 'url'}]}>
                            <Input type="url"/>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="Zweck" name={"purpose"} rules={[{type: 'string'}]}>
                    <AntdInput.TextArea autoSize={{maxRows:10}}/>
                </Form.Item>

                <Divider/>
                <Typography.Title level={4} type="secondary">
                    Übersetzungen
                    <Tooltip title="Übersetzung hinzufügen">
                        <Dropdown overlay={<Menu onClick={({key}) => onTranslationTabsEdit(key, 'add')}>
                            {Object.keys(translationLanguages).map(lang => <Menu.Item key={lang}>{translationLanguages[lang]}</Menu.Item>)}
                        </Menu>}>
                            <Button icon={<PlusCircleOutlined />} shape="circle" type="link"/>
                        </Dropdown>
                    </Tooltip>
                </Typography.Title>
                <Tabs
                    activeKey={activeTranslation}
                    animated
                    hideAdd
                    onChange={setActiveTranslation}
                    onEdit={onTranslationTabsEdit}
                    type="editable-card"
                >
                    {Object.keys(translations).map(key => (
                        <Tabs.TabPane key={key} tab={translationLanguages[key]}>
                            <Form.Item name={['translations', key, 'locale']} rules={[{type: 'string'}]} initialValue={key}>
                                <Input type="hidden"/>
                            </Form.Item>

                            <Row gutter={10} type="flex" justify="space-between">
                                <Col span={11}>
                                    <Form.Item label="Name" name={['translations', key, 'name']} rules={[{type: 'string'}]}>
                                        <Input/>
                                    </Form.Item>
                                </Col>
                                <Col span={11}>
                                    <Form.Item label="URL zu Vertragsbedingungen" name={['translations', key, 'policyUrl']} rules={[{type: 'url'}]}>
                                        <Input type="url"/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item label="Zweck" name={['translations', key, 'purpose']} rules={[{type: 'string'}]}>
                                <AntdInput.TextArea autoSize={{maxRows:10}}/>
                            </Form.Item>
                        </Tabs.TabPane>
                    ))}
                </Tabs>

                <Divider/>
                <Typography.Title level={4} type="secondary">
                    Regeln
                    <Dropdown overlay={<Menu onClick={({key}) => onThemeRules(key, 'add')}>
                        {RULES.map((rule, index) => <Menu.Item key={index}>{rule.variable.name}</Menu.Item>)}
                    </Menu>}>
                        <Button icon={<PlusCircleOutlined />} shape="circle" type="link"/>
                    </Dropdown>
                </Typography.Title>
                {Object.keys(themeRules).map((key, index) =>
                    <SiteServiceRule key={key} index={key} onRemove={onThemeRules} rule={themeRules[key]}/>
                )}

                <div style={{textAlign: 'right'}}>
                    <Button icon={<SaveOutlined />} size="large" type="primary" htmlType="submit">Speichern</Button>
                </div>
            </Form>

        </Spin>
    );
}));
